import React from 'react';
import { operationalRouteTemplate, recordOperationalEvent } from '../../../analytics/operationalRuntime';
import { failCurrentProductNavigation } from '../../../analytics/productAnalyticsRuntime';
import { captureProductException } from '../../../config/sentry';
import { clearSiteData, isStaleDeployChunkFailure, reloadForStaleDeployChunk } from '../../../lib/deployChunkRecovery';

type ProductErrorBoundaryState = {
  hasError: boolean;
  incidentId: string | null;
  error: Error | null;
  reloadStarted: boolean;
};

type CrossOriginFailureClass =
  | 'stale_deploy_chunk'
  | 'type_error'
  | 'reference_error'
  | 'other_error';

type CrossOriginDiagnosticGlobal = typeof globalThis & {
  __pinpointCrossOriginFailureClass?: CrossOriginFailureClass;
};

function classifyCrossOriginFailure(error: Error): CrossOriginFailureClass {
  if (isStaleDeployChunkFailure(error)) return 'stale_deploy_chunk';
  if (error instanceof TypeError) return 'type_error';
  if (error instanceof ReferenceError) return 'reference_error';
  return 'other_error';
}

function recordCrossOriginFailureClass(error: Error): void {
  if (import.meta.env.VITE_CROSS_ORIGIN_FAILURE_DIAGNOSTICS !== 'true') return;
  (globalThis as CrossOriginDiagnosticGlobal).__pinpointCrossOriginFailureClass =
    classifyCrossOriginFailure(error);
}

export class ProductErrorBoundary extends React.Component<
  { children?: React.ReactNode },
  ProductErrorBoundaryState
> {
  constructor(props: { children?: React.ReactNode }) {
    super(props);
    this.state = { hasError: false, incidentId: null, error: null, reloadStarted: false };
  }

  static getDerivedStateFromError(error: Error): ProductErrorBoundaryState {
    return { hasError: true, incidentId: crypto.randomUUID(), error, reloadStarted: false };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo): void {
    recordCrossOriginFailureClass(error);
    const reloadStarted = isStaleDeployChunkFailure(error) && reloadForStaleDeployChunk();
    if (reloadStarted) {
      this.setState({ reloadStarted: true });
      return;
    }
    if (import.meta.env.DEV) console.error(error);
    const incidentId = this.state.incidentId ?? crypto.randomUUID();
    const routeTemplate = operationalRouteTemplate(window.location.pathname);
    failCurrentProductNavigation(routeTemplate);
    captureProductException(error, {
      contexts: { react: { componentStack: info.componentStack } },
    });
    void recordOperationalEvent({
      event: 'product_route_error',
      navigationId: incidentId,
      routeTemplate,
      reasonCategory: 'route_load',
    });
  }

  render(): React.ReactNode {
    if (this.state.hasError && this.state.reloadStarted) {
      return (
        <div className="flex min-h-screen items-center justify-center text-slate-400" role="status">
          Loading latest version…
        </div>
      );
    }

    if (this.state.hasError) {
      return (
        <div
          className="min-h-screen flex items-center justify-center bg-slate-50 p-4"
          data-testid="error-boundary-fallback"
        >
          <div className="bg-white p-6 rounded-lg shadow-xl max-w-2xl w-full border border-red-200">
            <h1 className="text-2xl font-bold text-red-600 mb-4">Something went wrong</h1>
            <p className="text-slate-600 mb-4">
              The application encountered a critical error and could not render.
            </p>
            <p className="text-slate-500 text-sm mb-4">
              Incident{' '}
              <span data-testid="product-incident-id">{this.state.incidentId}</span>
            </p>
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 transition"
            >
              Reload page
            </button>
            <div className="mt-3">
              <button
                type="button"
                data-testid="error-boundary-reset"
                onClick={async () => {
                  if (!window.confirm(
                    "This clears this app's data on this device. You'll be signed out and any unsaved work will be lost. Practice already saved to your account is safe. Continue?",
                  )) return;
                  try {
                    await clearSiteData();
                  } finally {
                    window.location.reload();
                  }
                }}
                className="bg-slate-200 text-slate-800 px-4 py-2 rounded hover:bg-slate-300 transition"
              >
                Reset and reload
              </button>
              <p className="text-slate-500 text-sm mt-2">
                Signs you out and clears saved data on this device — use only if
                Reload page doesn&apos;t fix it.
              </p>
            </div>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
